import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {MixMaterial} from '@hconnect/tuc-processes/fields/mix/types'
import {EditNote} from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import {Box, Typography, List, ListItem, ListItemButton, ListItemText} from '@mui/material'
import React, {useMemo, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

interface MaterialListProps {
  materials: MixMaterial[]
  selectedMaterialId: string | undefined
  /** Optional: selected contract item position number for more precise selection */
  selectedContractItemPositionNumber?: string | undefined
  selectedContractNumber?: string | undefined
  onSelectMaterial: (
    materialId: string,
    contractItemPositionNumber: string,
    contractNumber: string
  ) => void
}

/**
 * Component that displays a scrollable list of materials with selection functionality
 * Redesigned to match the truck type list design pattern with CheckIcon and proper styling
 */
export const MaterialList: React.FC<MaterialListProps> = ({
  materials,
  selectedMaterialId,
  selectedContractItemPositionNumber,
  selectedContractNumber,
  onSelectMaterial
}) => {
  const {t} = useTranslation()
  // Get selected material variations to display under the selected material
  const {selectedMaterial, selectedMaterialVariations, defaultMaterial, defaultMaterialVariations} =
    useMix()

  // Check if we're still on the original material from the order
  const isOnDefaultMaterial = selectedMaterial?.materialId === defaultMaterial?.materialId

  // Ref for the selected item to enable auto-scroll
  const selectedItemRef = useRef<HTMLLIElement>(null)

  // Auto-scroll to selected item when component mounts
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, []) // Only run on mount

  // Compute the material variations line
  // Only fallback to default variations if we're still on the default material
  const materialVariationsLine = useMemo(() => {
    const variations =
      selectedMaterialVariations ?? (isOnDefaultMaterial ? defaultMaterialVariations : null)
    if (!variations) return null
    const parts: string[] = []
    const slumpValue = variations.slump?.description || variations.slump?.value
    if (slumpValue) {
      parts.push(`${slumpValue} ${t('orderIntake.materialVariations.slump', 'Slump')}`)
    }
    const setControlText = variations.setControl?.description || variations.setControl?.value
    if (setControlText) {
      parts.push(setControlText)
    }
    return parts.length > 0
      ? t('orderIntake.rmc.orderManagement.materialVariationsLinePrefix', '*with {{variations}}', {
          variations: parts.join('; ')
        })
      : null
  }, [selectedMaterialVariations, isOnDefaultMaterial, defaultMaterialVariations, t])

  /**
   * Check if a material is selected.
   * Uses both materialId and contractItemPositionNumber for precise matching
   * when contractItemPositionNumber is available.
   */
  const isMaterialSelected = (material: MixMaterial): boolean => {
    if (!selectedMaterialId) return false

    if (selectedContractNumber && selectedContractNumber !== material.contractNumber) {
      return false
    }

    // If we have contractItemPositionNumber, use it for precise matching
    if (selectedContractItemPositionNumber) {
      return (
        selectedMaterialId === material.materialId &&
        selectedContractItemPositionNumber === material.contractItemPositionNumber
      )
    }

    // Fallback to materialId only
    return selectedMaterialId === material.materialId
  }

  /**
   * Check if a material has actual variation options available (not just empty arrays)
   */
  const hasMaterialVariationOptions = (material: MixMaterial): boolean => {
    if (!material.materialVariations) return false
    const {slumps, setControls} = material.materialVariations
    return (slumps?.length ?? 0) > 0 || (setControls?.length ?? 0) > 0
  }

  return (
    <Box
      sx={{
        height: '300px',
        overflowY: 'auto',
        width: '100%',
        '&::-webkit-scrollbar': {
          width: '8px'
        }
      }}
    >
      <List
        sx={{
          padding: 0,
          width: '100%',
          bgcolor: 'background.paper'
        }}
        data-test-id="rmc-material-selection"
      >
        {materials.length > 0 ? (
          materials.map((material, index) => {
            const isSelected = isMaterialSelected(material)
            // Kept without contractNumber: E2E tests (HubTestAutomation) select by this test id.
            const uniqueKey = `${material.materialId}-${material.contractItemPositionNumber}`

            return (
              <ListItem
                key={`${material.contractNumber}-${uniqueKey}`}
                ref={isSelected ? selectedItemRef : undefined}
                sx={{
                  padding: 0,
                  width: '100%'
                }}
              >
                <ListItemButton
                  onClick={() =>
                    onSelectMaterial(
                      material.materialId || '',
                      material.contractItemPositionNumber,
                      material.contractNumber
                    )
                  }
                  selected={isSelected}
                  sx={{
                    backgroundColor: isSelected ? '#f2f8fd' : '#ffffff',
                    borderBottom: index === materials.length - 1 ? 'none' : '1px solid #e8ecf0',
                    padding: '12px 16px',
                    minHeight: 'auto',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#f2f8fd'
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#f2f8fd',
                      '&:hover': {
                        backgroundColor: '#f2f8fd'
                      }
                    }
                  }}
                  data-test-id={`rmc-material-option-${uniqueKey}`}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#29333d',
                          textAlign: 'left'
                        }}
                      >
                        {isSelected && material.materialVariations && materialVariationsLine
                          ? `${material.materialDescription}*`
                          : material.materialDescription}
                      </Typography>
                    }
                    secondary={
                      <>
                        {/* Show material variations for selected material only if material supports variations */}
                        {isSelected && material.materialVariations && materialVariationsLine && (
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#52667a',
                              textAlign: 'left'
                            }}
                          >
                            {materialVariationsLine}
                          </Typography>
                        )}
                        {/* Show customer material description if available */}
                        {material.customerMaterialDescription && (
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#52667a',
                              textAlign: 'left'
                            }}
                          >
                            {material.customerMaterialDescription}
                          </Typography>
                        )}
                      </>
                    }
                    sx={{
                      margin: 0,
                      flex: 1
                    }}
                  />
                  {/* Show EditNote icon if material has variations with actual options available */}
                  {hasMaterialVariationOptions(material) ? (
                    <EditNote data-test-id="EditNoteIcon" />
                  ) : null}
                  {isSelected && (
                    <CheckIcon
                      sx={{
                        color: '#016ad4',
                        fontSize: '20px',
                        marginLeft: '16px'
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            )
          })
        ) : (
          <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#52667a'
              }}
            >
              {t(
                'orderIntake.rmc.orderManagement.noMaterialsMatchSearch',
                'No materials match your search. Try different keywords.'
              )}
            </Typography>
          </Box>
        )}
      </List>
    </Box>
  )
}
